import { useEffect, useRef, useState } from "react";
import { type DebouncedState } from "use-debounce";

import { useRuntime } from "~/components/canvas/easel/runtime/useRuntime";
import { type CachedCell, type CellInfo, isJavaScriptFunctionBodyCell } from "~/models/cell";

import { VariableObserver } from "../../easel/runtime/VariableObserver";
import { type Result } from "../../easel/runtime/interfaces";

/**
 * Executes a cell with the canvas runtime, maintaining a cache of the current output of the cell,
 * so that it can be retrieved at any point, e.g., if a React component unmounts and re-mounts.
 *
 * Cells are cached by their `id` field. This field *must* be unique to the canvas.
 *
 * Optionally takes `cellUpdated` which can be used to flash a visual queue in the canvas UI to
 * indicate that a cell has been updated.
 */
export const useCachedCell = (
	cellInfo: CellInfo,
	cellUpdated?: DebouncedState<(pending: unknown) => void>
): CachedCell => {
	const runtime = useRuntime();

	const [boxedValue, setBoxedValue] = useState<{ result?: Result }>(() => {
		// IMPORTANT: Prevent flashing in the UI when the compute cell gets spuriously re-rendered,
		// e.g., via `flushSync`. Initialize with the current result of the cell if it's available.
		if (cellInfo.status !== "executable") {
			return {};
		}
		const cachedCell = runtime?.getOrCacheCell(cellInfo);
		return { result: cachedCell?.currentResult };
	});
	const observer = useRef<VariableObserver | undefined>();

	useEffect(() => {
		if (runtime === null) {
			console.warn("runtime is null");
			return;
		}

		if (cellInfo.status !== "executable") {
			return;
		}

		const cachedCell = runtime.getOrCacheCell(cellInfo);

		//
		// IMPORTANT: Dispose old observer or it could continue to call `setBoxedValue`.
		//

		observer.current !== undefined && observer.current.dispose();

		//
		// And add a new variable observer!
		//

		observer.current = new VariableObserver((result) => {
			setBoxedValue({ result });
		}, cellUpdated);
		cachedCell.setSubscriber(observer.current);

		return () => {
			//
			// IMPORTANT: Do *NOT* purge the cached cell! If you do, any time the component unmounts
			// it will need to be re-run from scratch.
			//

			observer.current?.dispose();
		};
	}, [
		runtime,
		cellInfo.status,
		cellInfo.cell.code,
		cellInfo.cell.type,
		isJavaScriptFunctionBodyCell(cellInfo.cell) && cellInfo.cell.cellName,
	]);

	return boxedValue;
};
